import { AccommodationCombined, AccommodationCombinedType } from '../components/Accommodations/Accommodation.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useJoyPlacePricingV2PriceInfo } from '../components/AccommodationsV2/components/JoyPlacePricingV2/useJoyPlacePricingV2PriceInfo';
import { useMemo } from 'react';

const formatPrice = (valueInMinorUnits?: number, digits?: number): number => {
  if (!valueInMinorUnits) return 0;
  return digits ? valueInMinorUnits / Math.pow(10, digits) : valueInMinorUnits;
};

export type AccommodationsPrices = Readonly<{
  joyPrice: number;
  joyPriceWins: boolean;
  providerPrices: {
    [provider: string]: number; // support future dynamic providers
  };
}>;

export const useAccommodationsPrices = (accommodation: AccommodationCombined): AccommodationsPrices | undefined => {
  // Check if the "My Own Link" feature is enabled
  const [isMyOwnLinkFeatureEnabled] = useIsFeatureEnabled('accommodationsMyOwnLinkEnabled');
  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');

  // Determine if the accommodation is considered a custom property
  const isCustomProperty = (isMyOwnLinkFeatureEnabled && accommodation.isCustomLinkEnabled) || (!isMyOwnLinkFeatureEnabled && Boolean(accommodation.customUrl));

  // Decide whether to skip cached price fetching logic for room block and custom property
  const skipPriceFetch = !showNewPriceComparisonUI || accommodation.type === AccommodationCombinedType.Roomblock || isCustomProperty || !accommodation.isFromAccommodationCatalog;

  const { priceData, rates } = useJoyPlacePricingV2PriceInfo(accommodation, skipPriceFetch);

  return useMemo(() => {
    if (skipPriceFetch) return;
    const joyPrice = formatPrice(priceData?.valueInMinorUnits, priceData?.digits);

    const { providerPrices, joyPriceWins } = rates.reduce<{
      providerPrices: AccommodationsPrices['providerPrices'];
      joyPriceWins: boolean;
    }>(
      (acc, rate) => {
        const key = rate.provider;
        if (key) {
          const formattedPrice = formatPrice(rate.priceInMinorUnits, rate.priceDigits);
          acc.providerPrices[key] = formattedPrice;

          if (formattedPrice > 0 && joyPrice >= formattedPrice) {
            acc.joyPriceWins = false;
          }
        }
        return acc;
      },
      {
        providerPrices: {},
        joyPriceWins: joyPrice > 0,
      },
    );

    return { providerPrices, joyPrice, joyPriceWins };
  }, [priceData?.valueInMinorUnits, priceData?.digits, rates, skipPriceFetch]);
};
